import React, { FC } from 'react'

import { buildContentfulSrcSet, getHomepageBanner } from '@smarty-nx/contentful'
import { BulletPointItem, ContentfulProcessor, Level } from '@smarty-nx/ds'
import cn from 'classnames'
import { Link } from 'gatsby'
import { Swiper, SwiperSlide } from 'swiper/react'

import classes from './herobanner.module.scss'
import { ContentfulLink } from '../../../components/contentful-link'

type HeroBannerType = ReturnType<typeof getHomepageBanner> & {
  benefits?: string[]
  subHeading?: string
  mainImageLink?: string
}

const banner: HeroBannerType = getHomepageBanner()
const bannerImageUrl = banner.mainImage.file.url
const MAIN_IMAGE_WIDTHS = [320, 550, 1100]
const MAIN_IMAGE_SIZES = '(min-width: 760px) 50vw, 100vw'
const CAROUSEL_IMAGE_WIDTHS = [150, 300]
const CAROUSEL_IMAGE_SIZES = '150px'

export const HeroBanner: FC = () => (
  <div
    className={cn('redesign-container', classes.root)}
    data-module-name="hero"
  >
    <div className={cn(classes.container)}>
      <div className={classes.left}>
        <h1 className={classes.heading}>{banner.heading}</h1>

        {!!banner.subHeading && (
          <div className={classes.subheading}>
            <ContentfulProcessor content={banner.subHeading} />
          </div>
        )}

        {!!banner.benefits?.length && (
          <div className={classes.benefits}>
            {banner.benefits.map((text) => (
              <BulletPointItem text={text} key={text} />
            ))}
          </div>
        )}

        <div className={classes.ctas}>
          <ContentfulLink link={banner.primaryCta} />
          <ContentfulLink link={banner.secondaryCta} level={Level.Secondary} />
        </div>
      </div>

      <div className={classes.right}>
        <div className={classes.mainImage}>
          {banner.mainImageLink ? (
            <ContentfulProcessor
              content={banner.mainImageLink}
              components={{
                a({ node: _, href, ref: _ref, ...props }) {
                  return (
                    <Link to={href || ''} {...props}>
                      <img
                        src={`${bannerImageUrl}?w=550`}
                        srcSet={buildContentfulSrcSet(
                          bannerImageUrl,
                          MAIN_IMAGE_WIDTHS
                        )}
                        sizes={MAIN_IMAGE_SIZES}
                        fetchPriority="high"
                        alt={banner.mainImage.title}
                        loading="eager"
                      />
                    </Link>
                  )
                },
              }}
            />
          ) : (
            bannerImageUrl.length > 0 && (
              <img
                src={`${bannerImageUrl}?fm=webp`}
                srcSet={buildContentfulSrcSet(
                  bannerImageUrl,
                  MAIN_IMAGE_WIDTHS
                )}
                sizes={MAIN_IMAGE_SIZES}
                fetchPriority="high"
                alt={banner.mainImage.title}
                loading="eager"
              />
            )
          )}
        </div>
      </div>
    </div>

    {banner.carousel && (
      <>
        {banner.carousel?.title && (
          <h2 className={classes.carouselTitle}>{banner.carousel?.title}</h2>
        )}
        <Swiper
          watchOverflow
          allowTouchMove
          slidesPerView="auto"
          navigation={false}
          spaceBetween={0}
          className={classes.swiper}
        >
          {banner.carousel?.items.map((item) => (
            <SwiperSlide key={item.type} className={classes.slide}>
              <img
                src={`${item.image?.url || ''}?fm=webp&w=200`}
                srcSet={buildContentfulSrcSet(
                  item.image.url,
                  CAROUSEL_IMAGE_WIDTHS
                )}
                sizes={CAROUSEL_IMAGE_SIZES}
                alt={item.image?.title || ''}
                loading="lazy"
                decoding="async"
              />
              {item?.description && (
                <div className={classes.description}>
                  <ContentfulProcessor content={item.description} />
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </>
    )}
  </div>
)
